import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Megaphone, ExternalLink, Sparkles, PhoneCall, Building2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { SUPPLIER_LABELS as LABELS, supplierLabel, supplierLabels } from "@/data/supplierCategories";

const EXAMPLES = ["A trainer to deliver safeguarding and MCA training on site", "Care planning software for a 30-bed home", "A GP clinical system with online consultations for a two-site practice", "Medical indemnity for consultants working under practising privileges", "Servicing and calibration for our defibrillators and vaccine fridges"];

function SponsorCard({ sp }) {
  const click = () => api.post(`/sponsors/${sp.id}/click`).catch((e) => logError("sponsor click", e));
  return (
    <a href={sp.url} target="_blank" rel="noreferrer sponsored" onClick={click} className="rounded-2xl border border-accent/30 bg-accent/5 p-5 flex flex-col hover:shadow-md transition-shadow" data-testid={`supplier-sponsor-${sp.id}`}>
      <div className="flex items-center justify-between mb-2">
        <Badge variant="secondary" className="bg-accent/15 text-accent border-0 text-[10px] uppercase tracking-wider">Sponsored partner</Badge>
        <span className="text-[11px] text-muted-foreground">{supplierLabel(sp.category)}</span>
      </div>
      <h3 className="font-display font-semibold text-lg">{sp.company}</h3>
      <p className="text-sm text-muted-foreground flex-1 mt-1 leading-relaxed">{sp.description}</p>
      <span className="mt-4 text-sm font-semibold text-primary inline-flex items-center gap-1">Visit website <ExternalLink className="w-3.5 h-3.5" /></span>
    </a>
  );
}

function Directory() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/suppliers/directory").then((r) => setItems(r.data)).catch((err) => logError("Directory failed", err)); }, []);
  if (!items) return null;
  const cats = [...new Set(items.flatMap((i) => i.categories?.length ? i.categories : [i.category]))].sort();
  const shown = filter === "all" ? items : items.filter((i) => (i.categories?.length ? i.categories : [i.category]).includes(filter));
  return (
    <section className="mt-14" data-testid="supplier-directory">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <span className="overline text-accent">Partner directory</span>
          <h2 className="font-display text-xl sm:text-2xl font-bold">Vetted partners</h2>
        </div>
        <Link to="/advertise" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="directory-advertise-link"><Megaphone className="w-4 h-4" /> List your business</Link>
      </div>
      {cats.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="directory-filters">
          {["all", ...cats].map((c) => (
            <button key={c} type="button" onClick={() => setFilter(c)} data-testid={`directory-filter-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{c === "all" ? "All partners" : supplierLabel(c)}</button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="directory-empty">No partners listed yet in this category.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((sp) => (
            <article key={sp.id} className="rounded-2xl border border-border bg-card p-5 flex flex-col" data-testid={`directory-partner-${sp.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-primary" /></div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{sp.company}</p>
                  <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-accent" /> Sponsored partner · {supplierLabels(sp)}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground flex-1">{sp.description}</p>
              <a href={sp.url} target="_blank" rel="noreferrer sponsored" onClick={() => api.post(`/sponsors/${sp.id}/click`).catch(() => {})}
                className="mt-4 text-sm font-semibold text-primary inline-flex items-center gap-1 hover:underline" data-testid={`directory-visit-${sp.id}`}>Visit website <ExternalLink className="w-3.5 h-3.5" /></a>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Suppliers() {
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState(null);
  const [need, setNeed] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/suppliers/categories").then((r) => setCats(r.data)).catch((e) => logError("categories", e)); }, []);

  const find = async (text) => {
    const q = (text ?? need).trim();
    if (q.length < 3) return;
    setNeed(q);
    setBusy(true);
    try {
      const { data } = await api.post("/suppliers/find", { need: q, category });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not search just now");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="suppliers-page">
      <span className="overline text-accent">Find a supplier</span>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 mb-3">Find a trusted supplier or service</h1>
      <p className="text-muted-foreground max-w-2xl mb-8">Tell the assistant what you need — a trainer, clinical system, medical devices, indemnity, a pharmacy — and it will explain what an inspector expects you to check, and show our vetted sponsored partners for that service.</p>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="supplier-categories">
        {cats.map((c) => (
          <button key={c.id} type="button" onClick={() => setCategory(category === c.id ? null : c.id)} data-testid={`supplier-cat-${c.id}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${category === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
            {LABELS[c.id] || c.id}{c.partners > 0 && <span className="ml-1.5 text-[10px] opacity-70">· {c.partners}</span>}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); find(); }} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <Textarea value={need} onChange={(e) => setNeed(e.target.value)} rows={3} placeholder="e.g. We need a trainer to deliver Oliver McGowan training for 25 staff in the North West" data-testid="supplier-need" />
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button type="submit" disabled={busy || need.trim().length < 3} data-testid="supplier-find-button">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Search className="w-4 h-4 mr-1.5" />} Find suppliers</Button>
          <span className="text-xs text-muted-foreground">Try:</span>
          {EXAMPLES.map((ex) => <button key={ex} type="button" onClick={() => find(ex)} className="text-xs text-primary hover:underline text-left">{ex}</button>)}
        </div>
      </form>

      {result && (
        <div className="mt-8 space-y-6" data-testid="supplier-results">
          {result.sponsors.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">{result.sponsors.map((sp) => <SponsorCard key={sp.id} sp={sp} />)}</div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border p-5 text-sm text-muted-foreground flex items-start gap-3" data-testid="supplier-no-partner">
              <Megaphone className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p>We don't have a vetted partner for this yet. <Link to="/escalate" className="text-primary hover:underline">Ask our compliance specialist for a recommendation</Link>, or if you supply this service, <Link to="/advertise" className="text-primary hover:underline">become a sponsored partner</Link>.</p>
            </div>
          )}
          <div className="rounded-2xl bg-secondary/50 border border-border p-6" data-testid="supplier-guidance">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5 mb-3"><Sparkles className="w-3.5 h-3.5 text-primary" /> What to check before you buy</p>
            <div className="text-sm leading-relaxed whitespace-pre-wrap">{result.reply}</div>
          </div>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5" /> Prefer a human? Call our compliance specialist on <a href="tel:01135222861" className="text-primary hover:underline">0113 522 2861</a>.</p>
        </div>
      )}
      <Directory />
    </div>
  );
}
